import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, distinctUntilChanged, map, takeUntil } from 'rxjs';
import { ChatState, ChatStateService } from '../../../../../services/chat-state.service';
import { RegionChatSelectionStore } from './region-chat-selection.store';

/** Where the panel sits: left or right on wide screens, top or bottom as a sheet on phones. */
export type RegionPanelPlacement = 'left' | 'right' | 'top' | 'bottom';

/** Viewport width at or below which the panel is a sheet; matches the stylesheet's media query. */
export const REGION_PANEL_SHEET_MAX_WIDTH = 600;

/** Widest the desktop panel gets, in pixels; matches the stylesheet's width. */
export const REGION_PANEL_WIDTH = 340;

/** Gap between the desktop panel and the edge of the preview, in pixels; matches the stylesheet. */
export const REGION_PANEL_MARGIN = 12;

/** Gap kept between the Preview toolbar and a panel placed under it, in pixels (Issue #2542). */
export const TOOLBAR_GAP = 8;

/** Chat opens and docks in an animated drawer, so placement is measured again once it has settled. */
const CHAT_LAYOUT_SETTLE_MS = 450;

/** A box in viewport pixels, as getBoundingClientRect reports it. */
export interface PanelBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * How many pixels of the desktop panel's width an obstacle would cover if the panel sat on this side.
 *
 * The panel is inset by the margin from the preview's edge and is at most REGION_PANEL_WIDTH wide.
 * An obstacle that does not reach the preview's height covers nothing.
 */
export function sidePanelOverlap(side: 'left' | 'right', container: PanelBox, obstacle: PanelBox): number {
  const width = Math.max(0, Math.min(REGION_PANEL_WIDTH, container.width - 2 * REGION_PANEL_MARGIN));
  const left =
    side === 'left'
      ? container.left + REGION_PANEL_MARGIN
      : container.left + container.width - REGION_PANEL_MARGIN - width;
  const overlapX = Math.min(left + width, obstacle.left + obstacle.width) - Math.max(left, obstacle.left);
  const overlapY =
    Math.min(container.top + container.height, obstacle.top + obstacle.height) - Math.max(container.top, obstacle.top);
  return overlapX > 0 && overlapY > 0 ? overlapX : 0;
}

/**
 * Chooses the panel position that keeps the selection visible (UX review of Issue #1747).
 *
 * On wide screens the panel goes to the side away from the selection's centre; on phones the sheet
 * goes to the top when the selection's centre is in the lower half of the preview, and to the
 * bottom otherwise. With no selection the default position is used.
 *
 * On wide screens the panel also avoids an obstacle drawn over the preview, such as the undocked
 * chat panel. When the preferred side is covered and the other side is less covered, the panel takes
 * the other side even if it then overlaps the selection: a panel whose Use in chat button is hidden
 * under chat is worse than one the user can collapse.
 */
export function choosePanelPlacement(
  viewportWidth: number,
  container: PanelBox | null,
  selection: PanelBox | null,
  obstacle: PanelBox | null = null
): RegionPanelPlacement {
  const sheet = viewportWidth <= REGION_PANEL_SHEET_MAX_WIDTH;
  if (!container) {
    return sheet ? 'bottom' : 'left';
  }
  const hasSelection = !!selection && !(selection.width === 0 && selection.height === 0);
  if (sheet) {
    return selection && hasSelection && selection.top + selection.height / 2 > container.top + container.height / 2
      ? 'top'
      : 'bottom';
  }
  const preferred: 'left' | 'right' =
    selection && hasSelection && selection.left + selection.width / 2 < container.left + container.width / 2
      ? 'right'
      : 'left';
  if (!obstacle) {
    return preferred;
  }
  const other = preferred === 'right' ? 'left' : 'right';
  const covered = sidePanelOverlap(preferred, container, obstacle);
  return covered > 0 && sidePanelOverlap(other, container, obstacle) < covered ? other : preferred;
}

/**
 * The chat panel's box while it floats over the page: open and not docked. Otherwise null.
 *
 * A docked chat panel pushes the preview aside instead, so it can never cover the region panel.
 */
export function undockedChatObstacle(
  state: Pick<ChatState, 'isOpen' | 'isDocked'> | null | undefined,
  chatPanel: Element | null
): PanelBox | null {
  if (!state?.isOpen || state.isDocked || !chatPanel) {
    return null;
  }
  const box = chatPanel.getBoundingClientRect();
  return box.width > 0 && box.height > 0
    ? { left: box.left, top: box.top, width: box.width, height: box.height }
    : null;
}

/** How far the Preview toolbar is shifted up while it is faded out; matches its stylesheet. */
const TOOLBAR_HIDDEN_SHIFT = 10;

/**
 * The box the Preview toolbar occupies when shown, or null when it is not rendered or has no size.
 * While faded out the toolbar is drawn slightly higher, so that shift is added back.
 */
export function toolbarBox(toolbar: Element | null): PanelBox | null {
  const content = toolbar?.querySelector('.toolbar-content') ?? toolbar;
  if (!toolbar || !content) return null;
  const box = content.getBoundingClientRect();
  if (box.width <= 0 || box.height <= 0) return null;
  const shift = toolbar.classList.contains('visible') ? 0 : TOOLBAR_HIDDEN_SHIFT;
  return { left: box.left, top: box.top + shift, width: box.width, height: box.height };
}

/** Where the panel is pinned and how tall it may grow, in pixels relative to the preview container. */
export interface RegionPanelLayout {
  placement: RegionPanelPlacement;
  top: number | null;
  bottom: number | null;
  maxHeight: number | null;
}

/**
 * Fits the panel inside the part of the preview that is actually on screen.
 *
 * The preview container can extend past the bottom of the viewport (short desktop windows, phones),
 * so positioning against the container alone can push the panel, and its Use in chat button, off
 * screen. The panel is therefore pinned inside the container's intersection with the viewport and
 * capped to that height; on phones the sheet is also capped at 40% of the viewport height. An obstacle
 * over the preview, such as the undocked chat panel, is passed on to choosePanelPlacement.
 *
 * The panel never covers the Preview toolbar (Issue #2542): the zoom percentage, the active tool and
 * its exit button stay visible and clickable while a selection is shown. A panel that would share
 * the toolbar's columns starts below it instead. The toolbar keeps its box while it is faded out,
 * so the space is reserved whether or not it is currently visible.
 */
export function computePanelLayout(
  viewport: { width: number; height: number },
  container: PanelBox | null,
  selection: PanelBox | null,
  obstacle: PanelBox | null = null,
  toolbar: PanelBox | null = null
): RegionPanelLayout {
  const sheet = viewport.width <= REGION_PANEL_SHEET_MAX_WIDTH;
  if (!container) {
    return { placement: sheet ? 'bottom' : 'left', top: null, bottom: null, maxHeight: null };
  }
  const visibleTop = Math.max(container.top, 0);
  const visibleBottom = Math.min(container.top + container.height, viewport.height);
  const visibleHeight = visibleBottom - visibleTop;
  if (visibleHeight <= 0) {
    return { placement: sheet ? 'bottom' : 'left', top: null, bottom: null, maxHeight: null };
  }
  const visible = { left: container.left, top: visibleTop, width: container.width, height: visibleHeight };
  const placement = choosePanelPlacement(viewport.width, visible, selection, obstacle);
  if (sheet) {
    const margin = 8;
    if (placement === 'top') {
      // The top sheet spans the preview, so it always starts below the toolbar row.
      const top = Math.max(visibleTop + margin, toolbar ? toolbar.top + toolbar.height + TOOLBAR_GAP : 0);
      const maxHeight = Math.max(0, Math.min(viewport.height * 0.4, visibleBottom - margin - top));
      return { placement, top: top - container.top, bottom: null, maxHeight };
    }
    const maxHeight = Math.max(0, Math.min(viewport.height * 0.4, visibleHeight - 2 * margin));
    return { placement, top: null, bottom: container.top + container.height - visibleBottom + margin, maxHeight };
  }
  const margin = REGION_PANEL_MARGIN;
  let top = visibleTop + margin;
  if (toolbar && (placement === 'left' || placement === 'right')) {
    // Only a panel that shares the toolbar's columns has to start below it. On a wide preview the
    // left panel and the right-aligned toolbar never meet.
    const width = Math.max(0, Math.min(REGION_PANEL_WIDTH, container.width - 2 * margin));
    const left = placement === 'left' ? container.left + margin : container.left + container.width - margin - width;
    const sharesColumns = left < toolbar.left + toolbar.width && toolbar.left < left + width;
    if (sharesColumns) top = Math.max(top, toolbar.top + toolbar.height + TOOLBAR_GAP);
  }
  return {
    placement,
    top: top - container.top,
    bottom: null,
    maxHeight: Math.max(0, visibleBottom - margin - top),
  };
}

/**
 * Reviews the stored image of an area before attaching it to chat. Its outline and measurements
 * belong to the area tool. Escape closes this review before other Preview handlers run.
 */
@Component({
  selector: 'app-region-chat-panel',
  imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './region-chat-panel.component.html',
  styleUrls: ['./region-chat-panel.component.scss'],
})
export class RegionChatPanelComponent implements OnInit, DoCheck, OnDestroy {
  readonly store = inject(RegionChatSelectionStore);
  private cdr = inject(ChangeDetectorRef);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private chatState = inject(ChatStateService);

  /** Current position; recomputed when the selection or chat layout changes, the page scrolls or the window resizes. */
  placement: RegionPanelPlacement = 'left';

  /** Inline top/bottom/max-height that keep the panel inside the on-screen part of the preview. */
  layout: RegionPanelLayout = { placement: 'left', top: null, bottom: null, maxHeight: null };

  /** Whether the panel is collapsed to its header so the drawing underneath stays visible. */
  collapsed = false;

  private lastSelectionRef: unknown = null;
  private lastCropStatus: string | null = null;
  private placementFrame: ReturnType<typeof setTimeout> | number | null = null;
  private composerFocusTimer: ReturnType<typeof setTimeout> | null = null;
  private chatLayoutTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onScrollCapture = (): void => this.schedulePlacement();

  /** Latest message for the polite live region. */
  announcement = '';

  private destroy$ = new Subject<void>();
  private readonly onKeydownCapture = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || (!this.store.active && !this.store.restored)) return;
    // Escape inside an open menu or dialog belongs to that overlay; it must not also clear the
    // selection underneath (Issue #2542).
    if ((event.target as Element | null)?.closest?.('.cdk-overlay-container')) return;
    if (this.store.escape()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.cdr.markForCheck();
    }
  };

  ngOnInit(): void {
    this.store.announcements$.pipe(takeUntil(this.destroy$)).subscribe(message => {
      // Re-announce identical text: clear first so screen readers notice the change.
      this.announcement = '';
      this.cdr.detectChanges();
      this.announcement = message;
      this.cdr.markForCheck();
    });
    this.store.changes$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
    this.store.activeChanged$.pipe(takeUntil(this.destroy$)).subscribe(active => {
      if (active) this.collapsed = false;
    });
    // Opening, closing, docking or undocking chat changes what covers the preview.
    this.chatState.state
      .pipe(
        map(state => `${state.isOpen}:${state.isDocked}`),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.onChatLayoutChanged());
    document.addEventListener('keydown', this.onKeydownCapture, true);
    document.addEventListener('scroll', this.onScrollCapture, true);
  }

  /** Recomputes the placement whenever the selection or restored region is replaced. */
  ngDoCheck(): void {
    const selection = this.store.restored ?? this.store.polygon;
    if (selection !== this.lastSelectionRef || this.store.cropStatus !== this.lastCropStatus) {
      this.lastSelectionRef = selection;
      this.lastCropStatus = this.store.cropStatus;
      this.schedulePlacement();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.schedulePlacement();
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.onKeydownCapture, true);
    document.removeEventListener('scroll', this.onScrollCapture, true);
    if (this.composerFocusTimer) clearTimeout(this.composerFocusTimer);
    if (this.chatLayoutTimer) clearTimeout(this.chatLayoutTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get cropCaption(): string {
    const crop = this.store.crop;
    if (!crop) return '';
    return `${crop.width} × ${crop.height} px · ${crop.pageRegion.renderDpi} dpi`;
  }

  get previewAlt(): string {
    const crop = this.store.crop;
    return crop ? `Image that will be sent: ${crop.pageRegion.label}` : '';
  }

  /** Attaches the crop, then moves keyboard focus to the chat composer once chat has opened. */
  useInChat(): void {
    if (this.store.useInChat()) {
      this.focusChatComposer();
    }
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
  }

  /** Measures the selection and the visible preview, and updates the panel position if it changed. */
  updatePlacement(): void {
    const container = this.host.nativeElement.closest('.preview-container');
    const outline = container?.querySelector(
      '[data-testid="region-chat-outline"], [data-testid="region-chat-restored-outline"]'
    );
    const next = computePanelLayout(
      { width: window.innerWidth, height: window.innerHeight },
      container ? container.getBoundingClientRect() : null,
      outline ? outline.getBoundingClientRect() : null,
      undockedChatObstacle(this.chatState.currentState, document.querySelector('.chat-panel')),
      toolbarBox(container?.querySelector('[data-testid="preview-toolbar"]') ?? null)
    );
    const current = this.layout;
    if (
      next.placement !== current.placement ||
      next.top !== current.top ||
      next.bottom !== current.bottom ||
      next.maxHeight !== current.maxHeight
    ) {
      this.layout = next;
      this.placement = next.placement;
      this.cdr.markForCheck();
    }
  }

  /** Places the panel now, and again once the chat drawer has finished animating. */
  private onChatLayoutChanged(): void {
    this.schedulePlacement();
    if (this.chatLayoutTimer) clearTimeout(this.chatLayoutTimer);
    this.chatLayoutTimer = setTimeout(() => {
      this.chatLayoutTimer = null;
      this.schedulePlacement();
    }, CHAT_LAYOUT_SETTLE_MS);
  }

  private schedulePlacement(): void {
    if (this.placementFrame !== null) return;
    const run = () => {
      this.placementFrame = null;
      this.updatePlacement();
    };
    this.placementFrame =
      typeof requestAnimationFrame === 'function' ? requestAnimationFrame(run) : setTimeout(run, 16);
  }

  /**
   * Focuses the chat composer. Chat opens in a drawer that renders asynchronously, so the textarea is
   * polled for briefly rather than assumed to exist at the moment of attaching.
   */
  private focusChatComposer(attempt = 0): void {
    const textarea = document.querySelector<HTMLTextAreaElement>('.chat-panel app-chat-input textarea');
    if (textarea && !textarea.disabled && textarea.getClientRects().length > 0) {
      textarea.focus();
      return;
    }
    if (attempt < 30) {
      this.composerFocusTimer = setTimeout(() => this.focusChatComposer(attempt + 1), 50);
    }
  }

  copyImage(): void {
    void this.store.copyImage();
  }

  retry(): void {
    void this.store.requestCrop();
  }

  close(): void {
    if (this.store.active) {
      this.store.deactivate(true);
    } else {
      this.store.restored = null;
    }
  }
}
